<template>
  <div
    :class="[$style.switchBar, { [$style.switchBarWithMap]: viewMode === ViewMode.Map }]"
    data-testid="switch-bar"
  >
    <HgSegmentedButton
      v-model="defaultViewMode"
      name="view-mode-mobile"
      :options="viewModeOptions"
      theme="secondary"
      :class="$style.switchButton"
      @update:modelValue="onViewModeChange"
    />
    <HgButton
      :class="$style.searchButton"
      theme="secondary-bordered"
      name="searchButton"
      icon="Search"
      :disabled="isLoading"
      @click="handleSearchIconClick"
    />
    <FiltersModal
      :visible="isSearchModalVisible"
      @onCancelClick="handleModalClose"
      @onSearchComplete="handleModalClose"
    />
  </div>
</template>

<script lang="ts" setup>
import { HgButton, HgSegmentedButton, type HgSegmentedOptions } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import FiltersModal from '@/components/search-bar/small-screen/FiltersModal.vue';
import useMapStore from '@/stores/useMapStore';
import { ViewMode } from '@/types';

const { t } = useI18n();

const mapStore = useMapStore();
const { isLoading, viewMode } = storeToRefs(mapStore);

const isSearchModalVisible = ref<boolean>(false);
const defaultViewMode = ref(viewMode);

const viewModeOptions = computed<Array<HgSegmentedOptions>>(() => [
  { value: ViewMode.Map, text: t('common.map') },
  { value: ViewMode.Data, text: t('common.data') },
]);

const onViewModeChange = (value: string | number): void => {
  const mode = String(value) as ViewMode;
  mapStore.setViewMode(mode);
};

const handleSearchIconClick = (): void => {
  isSearchModalVisible.value = true;
};

const handleModalClose = (): void => {
  isSearchModalVisible.value = false;
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

$switch-button-size: 44px;

.switchBar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  gap: $space-75;
  justify-content: center;
  width: 100%;
  padding-top: $space-150;
  padding-bottom: $space-100;
  background: $color-white;

  @include grid-media-query(sm) {
    display: none;
  }

  .switchButton {
    width: calc(100% - #{$switch-button-size} - 3 * #{$space-100});
    padding: 0;
  }

  > .searchButton {
    gap: 0;
    width: $switch-button-size;
    min-width: $switch-button-size;
    height: $switch-button-size;

    > svg {
      padding-right: 0;
      padding-left: $space-50;
    }
  }
}

.switchBarWithMap {
  position: absolute;
  top: $header-height-sm;
  background: transparent;
}
</style>
